/**
 * @description SignUp component for user registration (dark theme)
 *
 * Key features:
 * - Registration form (email, first name, last name — no password)
 * - Calls POST /api/auth/signup
 * - Shows "Check your email" message on success
 * - Password is set later on AcceptInvite page after clicking verification email
 * - Brand accent colors via CSS custom properties (--brand-primary)
 *
 * @dependencies
 * - AuthLayout: Shared auth page layout (dual-mode: LynxPay marketing / tenant simple)
 * - BootSplashDismisser: hides HTML #boot-splash after the form commits
 * - UnsignedSignInRedirect: off-path bounce restores dismissed #auth-boot-splash
 * - apiClient: API calls to backend
 *
 * @notes
 * - Off (LYN-320): /sign-up sends the visitor to /sign-in via UnsignedSignInRedirect
 * - After signup, user receives branded verification email via Resend
 * - Email contains link to /accept-invite?passwordResetCode=...&context=signup
 * - User sets password on AcceptInvite page, then auto-logs in via PKCE
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Zap, BarChart3, Globe } from 'lucide-react';
import apiClient from '../../services/api/apiClient';
import AuthLayout from './AuthLayout';
import { BootSplashDismisser, BootSplashKeeper, UnsignedSignInRedirect } from './AuthBootSplash';
import { useBranding } from '../../contexts/BrandingContext';
import { isSignupChromeAllowed } from '../../utils/authSignupChrome';
import { resolveSignUpError } from '../../utils/authErrorMessages';
import { resolveAuthSubdomain, withAuthSubdomain } from '../../utils/authTenantQuery';

function SignUp() {
  const { isLoading: isBrandingLoading, slug: brandingSlug, signupEnabled } = useBranding();
  const showSignupChrome = isSignupChromeAllowed({ signupEnabled });
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(null);

  // Features for left marketing panel
  const signUpFeatures = [
    {
      icon: Shield,
      title: 'Enterprise security',
      description: 'Bank-grade encryption and PCI-DSS compliance built in.',
    },
    {
      icon: Zap,
      title: 'Instant setup',
      description: 'Get your merchant dashboard running in minutes.',
    },
    {
      icon: BarChart3,
      title: 'Real-time analytics',
      description: 'Track revenue, expenses, and growth at a glance.',
    },
    {
      icon: Globe,
      title: 'Multi-location',
      description: 'Manage all your business locations from one place.',
    },
  ];

  /**
   * Validate form fields
   * @returns {boolean} Whether form is valid
   */
  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle input change and clear field error
   */
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }

    // Clear general error
    if (error) {
      setError(null);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const subdomain = await resolveAuthSubdomain({ brandingSlug });
      const signupUrl = withAuthSubdomain('/auth/signup', subdomain);

      // Call backend signup endpoint
      const response = await apiClient.post(signupUrl, {
        email: formData.email.trim().toLowerCase(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim()
      });

      if (response.data.success) {
        // Show verification message
        setSuccess({
          title: 'Check your email',
          message: 'We sent a verification link to ' + formData.email + '. Please check your email to set up your password and get started.'
        });
      } else {
        setError('Unexpected response from server. Please try again.');
      }

    } catch (err) {
      console.error('Signup error:', err);

      const errorCode = err.response?.data?.error?.code;
      const errorMessage = err.response?.data?.error?.message;
      const errorField = err.response?.data?.error?.field;

      const { generalMessage, fieldErrors: apiFieldErrors } = resolveSignUpError({
        errorCode,
        errorMessage,
        errorField,
        httpStatus: err.response?.status,
      });

      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors(prev => ({ ...prev, ...apiFieldErrors }));
      }

      if (generalMessage) {
        setError(generalMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Wait for branding to resolve before rendering AuthLayout — prevents flash
  // of LynxPay marketing layout on tenant subdomains (same pattern as SignIn).
  // HTML #boot-splash stays up; do not paint a React splash.
  if (isBrandingLoading) {
    return <BootSplashKeeper />;
  }

  if (!showSignupChrome) {
    return <UnsignedSignInRedirect />;
  }

  // Show success state
  if (success) {
    return (
      <>
      <BootSplashDismisser ready />
      <AuthLayout
        helper={null}
        title={<>Start growing<br /><span className="text-brand">your business.</span></>}
        subtitle="Join thousands of merchants managing their operations with LynxPay."
        features={signUpFeatures}
      >
        <div className="flex flex-col items-center text-center py-8">
          <div className="w-16 h-16 rounded-full bg-brand/10 border border-brand/20 flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {success.title}
          </h1>
          <p className="text-gray-400 mb-8 text-sm">
            {success.message}
          </p>
          <Link
            to="/sign-in"
            className="text-sm font-bold text-brand hover:underline underline-offset-4 transition-all"
          >
            Go to sign in
          </Link>
        </div>
      </AuthLayout>
      </>
    );
  }

  return (
    <>
    <BootSplashDismisser ready />
    <AuthLayout
      title={<>Start growing<br /><span className="text-brand">your business.</span></>}
      subtitle="Join thousands of merchants managing their operations with LynxPay."
      features={signUpFeatures}
      helper={
        <div className="flex items-center justify-center gap-2 text-sm">
          <span className="text-gray-500">Already have an account?</span>
          <Link
            to="/sign-in"
            className="font-bold text-brand hover:underline underline-offset-4 transition-all"
          >
            Sign in
          </Link>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-white mb-2">Create your account</h1>
          <p className="text-gray-500 text-sm font-medium">Get started today</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Alert */}
          {error && (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
              <span className="flex-1">{error}</span>
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-300 transition-colors"
                aria-label="Dismiss error"
              >
                <span className="text-lg leading-none">&times;</span>
              </button>
            </div>
          )}

          {/* Name Fields - Side by Side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] ml-1">First name</label>
              <input
                type="text"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className={`w-full bg-white/[0.03] border rounded-2xl px-4 py-3.5 text-white placeholder-gray-600 transition-all focus:bg-white/[0.06] focus:ring-4 outline-none ${
                  fieldErrors.firstName
                    ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/10'
                    : 'border-white/10 focus:border-brand/50 focus:ring-brand/10'
                }`}
                placeholder="John"
                disabled={isSubmitting}
              />
              {fieldErrors.firstName && (
                <p className="text-xs text-red-400 ml-1">{fieldErrors.firstName}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] ml-1">Last name</label>
              <input
                type="text"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className={`w-full bg-white/[0.03] border rounded-2xl px-4 py-3.5 text-white placeholder-gray-600 transition-all focus:bg-white/[0.06] focus:ring-4 outline-none ${
                  fieldErrors.lastName
                    ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/10'
                    : 'border-white/10 focus:border-brand/50 focus:ring-brand/10'
                }`}
                placeholder="Doe"
                disabled={isSubmitting}
              />
              {fieldErrors.lastName && (
                <p className="text-xs text-red-400 ml-1">{fieldErrors.lastName}</p>
              )}
            </div>
          </div>

          {/* Email Field */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] ml-1">Email address</label>
            <input
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`w-full bg-white/[0.03] border rounded-2xl px-4 py-3.5 text-white placeholder-gray-600 transition-all focus:bg-white/[0.06] focus:ring-4 outline-none ${
                fieldErrors.email
                  ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/10'
                  : 'border-white/10 focus:border-brand/50 focus:ring-brand/10'
              }`}
              placeholder="you@company.com"
              disabled={isSubmitting}
            />
            {fieldErrors.email && (
              <p className="text-xs text-red-400 ml-1">{fieldErrors.email}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-brand text-[#0a0a0b] font-bold rounded-2xl px-4 py-4 text-sm tracking-tight transition-all hover:bg-brand-hover active:scale-[0.98] disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2 shadow-2xl mt-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                Creating account...
              </>
            ) : (
              'Create account'
            )}
          </button>
        </form>

        {/* Terms notice */}
        <p className="text-center text-[10px] text-gray-600 leading-relaxed max-w-[280px] mx-auto">
          By creating an account, you agree to our{' '}
          <Link to="/terms" className="text-brand font-bold hover:underline">Terms</Link> and{' '}
          <Link to="/privacy" className="text-brand font-bold hover:underline">Privacy Policy</Link>.
        </p>
      </div>
    </AuthLayout>
    </>
  );
}

export default SignUp;
